import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase/config";

export const APPLICATION_STATUSES = [
  "new",
  "pending",
  "shortlisted",
  "interview",
  "accepted",
  "hired",
  "rejected",
];

export async function fetchAllJobPosts() {
  const jobPostsRef = collection(db, "recruitmentSystem", "jobPosts", "JobPosts");
  const snapshot = await getDocs(query(jobPostsRef, orderBy("createdAt", "desc")));
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}

export async function fetchJobPostById(jobId) {
  const jobDocRef = doc(db, "recruitmentSystem", "jobPosts", "JobPosts", jobId);
  const snap = await getDoc(jobDocRef);
  if (!snap.exists()) return null;
  return { id: jobId, ...snap.data() };
}

export async function fetchApplicationsByJob(jobId, options = {}) {
  const { status } = options;
  const applicationsRef = collection(
    db,
    "recruitmentSystem",
    "jobPosts",
    "JobPosts",
    jobId,
    "applications"
  );

  let q;
  if (status) {
    q = query(applicationsRef, where("status", "==", status), orderBy("createdAt", "desc"));
  } else {
    q = query(applicationsRef, orderBy("createdAt", "desc"));
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}

export async function fetchAllApplications(options = {}) {
  const { status = "all", searchTerm = "", jobId = "all" } = options;
  const jobs = await fetchAllJobPosts();
  const targetJobs = jobId === "all" ? jobs : jobs.filter((item) => item.id === jobId);

  const rows = [];
  await Promise.all(
    targetJobs.map(async (job) => {
      const apps = await fetchApplicationsByJob(
        job.id,
        status !== "all" ? { status } : {}
      );
      apps.forEach((app) => {
        rows.push({
          ...app,
          sourceJobId: job.id,
          sourceJobTitle: job.title || "",
          sourceCompany: job.company || "",
        });
      });
    })
  );

  const normalizedSearch = String(searchTerm || "").trim().toLowerCase();
  const filtered = normalizedSearch
    ? rows.filter((app) => {
        const fullName = `${app.firstName || ""} ${app.lastName || ""}`.trim().toLowerCase();
        return (
          fullName.includes(normalizedSearch) ||
          String(app.email || "").toLowerCase().includes(normalizedSearch) ||
          String(app.phone || "").toLowerCase().includes(normalizedSearch) ||
          String(app.sourceJobTitle || "").toLowerCase().includes(normalizedSearch)
        );
      })
    : rows;

  filtered.sort((a, b) => {
    const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : new Date(a.createdAt || 0).getTime();
    const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : new Date(b.createdAt || 0).getTime();
    return bTime - aTime;
  });
  return filtered;
}

export async function updateApplicationStatus({ jobId, applicationId, status }) {
  const applicationRef = doc(
    db,
    "recruitmentSystem",
    "jobPosts",
    "JobPosts",
    jobId,
    "applications",
    applicationId
  );
  await updateDoc(applicationRef, {
    status,
    statusUpdatedAt: new Date(),
    updatedAt: new Date(),
  });
}

export async function fetchApplicationBySource(jobId, applicationId) {
  if (!jobId || !applicationId) return null;
  const applicationRef = doc(
    db,
    "recruitmentSystem",
    "jobPosts",
    "JobPosts",
    jobId,
    "applications",
    applicationId
  );
  const appSnap = await getDoc(applicationRef);
  if (!appSnap.exists()) return null;

  const job = await fetchJobPostById(jobId);
  return {
    id: appSnap.id,
    ...appSnap.data(),
    sourceJobId: jobId,
    sourceJobTitle: job?.title || "",
    sourceCompany: job?.company || "",
  };
}
